import { type FC } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { renderToString } from 'react-dom/server'
import { Icon, Typography } from 'shared/ui'
import { parserHtml, formatCallDuration } from 'shared/lib'
import { getAttachmentType } from 'entities/Attachment'
import { type Conversation } from 'entities/Conversation/model/Conversation'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ConversationCardFiles } from 'entities/Conversation/ui/ConversationCard/ui/ConversationCardFiles/ConversationCardFiles'
import { ConversationCardCall } from 'entities/Conversation/ui/ConversationCard/ui/ConversationCardCall/ConversationCardCall'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { parseMentionsTemplate, parseNoteBody } from 'entities/Message/lib/parseNote'
import styles from '../styles.module.scss'

type IConversationMessageProps = {
  className?: string
  item: Conversation
}

export const ConversationMessage: FC<IConversationMessageProps> = observer(
  ({ item, className }) => {
    const { message: originMessage, recentMentionMessage } = item
    const entities = useEntitiesContext()
    const { conversationStore, usersStore, inboxesStore } = entities
    const draftMessage = conversationStore.getItemDraft(item.id)

    const message =
      inboxesStore.localInboxes.hasFilterMentions && recentMentionMessage
        ? recentMentionMessage
        : originMessage

    if (draftMessage) {
      if (draftMessage?.attachments_count) {
        return (
          <ConversationCardFiles
            type={getAttachmentType({
              content_type: draftMessage?.attachment_type || message?.attachment_type || '',
            })}
            count={draftMessage?.attachments_count || message?.attachments_count || 0}
            label='Draft:'
          />
        )
      }

      return (
        <Typography
          className={classnames(styles.body, className)}
          variant={'body-md-regular'}
          ellipsis
          ariaLabel={'body'}
        >
          <span className={styles.body__label}>Draft:</span>
          <span
            dangerouslySetInnerHTML={{
              __html: renderToString(
                <>{parserHtml(parseNoteBody(draftMessage.body, usersStore.usersMap))}</>
              ),
            }}
          />
        </Typography>
      )
    }

    if (message?.callListPreview) {
      return (
        <ConversationCardCall
          {...message.callListPreview}
          duration={message.duration}
          className={className}
        />
      )
    }

    if (message?.isFailed) {
      const failedIcon = (
        <Icon
          className={styles.failedIcon}
          icon={'close'}
          fontSize={16}
          color={'var(--content-negative-primary)'}
        />
      )

      if (message?.attachments_count) {
        return (
          <div className={styles.failedWrap}>
            {failedIcon}
            <ConversationCardFiles
              type={getAttachmentType({
                content_type: message?.attachment_type || message?.attachment_type || '',
              })}
              count={message?.attachments_count || message?.attachments_count || 0}
              label='Failed:'
            />
          </div>
        )
      }

      return (
        <div className={styles.failedWrap}>
          {failedIcon}
          <Typography
            className={classnames(styles.body, className)}
            variant={'body-md-regular'}
            ellipsis
            ariaLabel={'body'}
          >
            <span className={styles.body__label}>Failed:</span>
            <span
              dangerouslySetInnerHTML={{ __html: renderToString(<>{parserHtml(message.body)}</>) }}
            />
          </Typography>
        </div>
      )
    }

    if (message?.attachments_count) {
      return (
        <ConversationCardFiles
          type={getAttachmentType({
            content_type: message?.attachment_type || '',
          })}
          count={message?.attachments_count || 0}
        />
      )
    }

    let bodyParse = textToHtml(entities, {
      text: message?.bodyParse || '',
      replaceNewRow: false,
      noReplaceMergeField: true,
      noEscapeHtml: message?.isAirCall,
    })

    if (!message?.isNote) bodyParse = parseMentionsTemplate(bodyParse, usersStore.usersMap)
    bodyParse = parseNoteBody(bodyParse, usersStore.usersMap)

    // For events from a chatbot.
    // In the future, if similar cases appear, the logic/getting fields from the API will be changed
    if (message?.bodyParse?.startsWith('{&quot')) {
      bodyParse = 'Booking info'
    }

    return (
      <Typography
        className={classnames(styles.body, className)}
        variant={'body-md-regular'}
        ellipsis
        ariaLabel={'body'}
      >
        <span className={styles.body__label}></span>
        <span dangerouslySetInnerHTML={{ __html: renderToString(<>{parserHtml(bodyParse)}</>) }} />
        {message?.type === 'call' && !!message?.duration && (
          <span> · {formatCallDuration(message.duration)}</span>
        )}
      </Typography>
    )
  }
)
